import { useMemo } from 'react';
import { useGetRfiTypes } from './queries/rfiTypesQueries';
import featureFlags from '../helpers/featureFlags';
import { FEATURE_FLAGS } from '../constants/featureFlags';
import { ISSUE_ATTRIBUTE_NAMES } from '../constants/issue-attribute-names';
import { RfiStatus } from '../sdk';
import {
  ManageFieldType,
  StandardManageFieldType,
  CustomManageFieldType,
  RfiType,
} from '../sdk/types/common/rfiType';

/**
 * Field names whose required-by-status logic is the only authority for "this field
 * is mandatory". The new manage-fields-mandatory pipeline must skip these names so
 * we never have two `formik.errors[name]` writers fighting over the same key.
 *
 * See SCPJM-36295 for full trade-off analysis.
 */
export const CONSTANT_MANDATORY_FIELDS: readonly string[] = [
  ISSUE_ATTRIBUTE_NAMES.STATUS,
  ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER,
  ISSUE_ATTRIBUTE_NAMES.TITLE,
  ISSUE_ATTRIBUTE_NAMES.RFI_TYPE_ID,
  ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO,
];

export const isStandardField = (field: ManageFieldType): field is StandardManageFieldType =>
  (field as StandardManageFieldType).fieldName !== undefined;

export const isCustomField = (field: ManageFieldType): field is CustomManageFieldType =>
  (field as CustomManageFieldType).fieldUuid !== undefined;

export type ManageFieldsMap = Record<string, ManageFieldType>;

export type UseManageFieldsReturn = {
  /**
   * True iff the lib feature flag `ab-rfis-lib-manage-fields` is enabled.
   * When false, all selectors below act as no-ops (everything visible, nothing
   * mandatory) so call sites do not need to branch on the FF themselves.
   */
  isFFEnabled: boolean;
  /** Raw map keyed by `fieldName` (standard) or `fieldUuid` (custom). */
  map: ManageFieldsMap;
  /**
   * True if the standard field is visible.
   *
   * Missing fields default to **visible**, so fields the API contract
   * marks `canToggleVisibility: false` (e.g. `question`, `dueDate`) stay
   * visible by virtue of the BE never including them in `manageFields`.
   * Only the five `CONSTANT_MANDATORY_FIELDS` are short-circuited to
   * `true` — they are the always-render guarantee enforced in code.
   */
  isVisible: (fieldName: string) => boolean;
  /**
   * True if the standard field is mandatory per the RFI type's manageFields.
   *
   * Always returns false for the five core fields — their required-by-status
   * pipeline (`getMandatoryFields(selectedTransition)`) is the single
   * authority. Missing fields default to **non-mandatory**.
   */
  isMandatory: (fieldName: string) => boolean;
  /** True if the custom attribute is visible. Missing fields default to visible. */
  isCustomVisible: (fieldUuid: string) => boolean;
  /** True if the custom attribute is mandatory per the RFI type's manageFields. */
  isCustomMandatory: (fieldUuid: string) => boolean;
  /** Names of standard fields explicitly hidden by the active manageFields. */
  hiddenStandardNames: string[];
  /** UUIDs of custom attributes explicitly hidden by the active manageFields. */
  hiddenCustomIds: string[];
};

// Frozen so consumers that destructure and accidentally mutate (e.g. a test
// doing `result.current.map.foo = ...`) cannot corrupt the shared singleton.
const NOOP_RETURN: UseManageFieldsReturn = Object.freeze({
  isFFEnabled: false,
  map: Object.freeze({}) as ManageFieldsMap,
  isVisible: () => true,
  isMandatory: () => false,
  isCustomVisible: () => true,
  isCustomMandatory: () => false,
  hiddenStandardNames: Object.freeze([]) as readonly string[] as string[],
  hiddenCustomIds: Object.freeze([]) as readonly string[] as string[],
});

/**
 * Re-exported as a test fixture so spec files don't redeclare the no-op
 * return shape (which silently drifts when the selector surface grows).
 */
export const NOOP_USE_MANAGE_FIELDS_RETURN: UseManageFieldsReturn = NOOP_RETURN;

const buildMap = (manageFields: ManageFieldType[] | undefined): ManageFieldsMap => {
  if (!manageFields?.length) return {};
  return manageFields.reduce<ManageFieldsMap>((acc, field) => {
    if (isStandardField(field) && field.fieldName) {
      acc[field.fieldName] = field;
    } else if (isCustomField(field) && field.fieldUuid) {
      acc[field.fieldUuid] = field;
    }
    return acc;
  }, {});
};

const collectHiddenLists = (
  map: ManageFieldsMap,
): { hiddenStandardNames: string[]; hiddenCustomIds: string[] } => {
  const hiddenStandardNames: string[] = [];
  const hiddenCustomIds: string[] = [];
  Object.entries(map).forEach(([key, field]) => {
    if (field.isVisible !== false) return;
    if (isCustomField(field)) {
      hiddenCustomIds.push(key);
    } else if (!CONSTANT_MANDATORY_FIELDS.includes(key)) {
      hiddenStandardNames.push(key);
    }
  });
  return { hiddenStandardNames, hiddenCustomIds };
};

export type ResolvedActiveManageFields = {
  map: ManageFieldsMap;
  hiddenStandardNames: string[];
  hiddenCustomIds: string[];
};

/**
 * Pure resolver shared by `useManageFields` (the React hook) and by code paths
 * that cannot call hooks — e.g. the `yup.lazy(...)` schema callback and the
 * `createRfi` submit handler in `RfiFormProvider.tsx`. Centralizes:
 *
 * - Draft swaps `rfiTypeId` for the default type's id.
 * - The map keyed by `fieldName | fieldUuid`.
 * - The hidden lists, with the five core fields excluded from
 *   `hiddenStandardNames` so callers can never accidentally hide them.
 *
 * Returns the no-op shape (`map: {}`, empty hidden lists) when `rfiTypes` is
 * undefined or the resolved id has no matching type — callers can consume the
 * result unconditionally.
 */
export const resolveActiveManageFields = (
  rfiTypes: RfiType[] | undefined,
  rfiTypeId: string | undefined,
  status: RfiStatus | undefined,
): ResolvedActiveManageFields => {
  const effectiveTypeId =
    status === RfiStatus.Draft ? rfiTypes?.find((t) => t.isDefault)?.id : rfiTypeId;
  const rfiType = rfiTypes?.find((t) => t.id === effectiveTypeId);
  const map = buildMap(rfiType?.manageFields);
  const { hiddenStandardNames, hiddenCustomIds } = collectHiddenLists(map);
  return { map, hiddenStandardNames, hiddenCustomIds };
};

/**
 * Single source of truth for the mandatory-by-type concept introduced by
 * `manageFields[]` on the RFI type API.
 *
 * - Draft swap: when status is Draft and no `rfiTypeId` is selected, the
 *   default RFI type's `manageFields` is used (so Draft visibility/mandatory is
 *   consistent with what the user will see after picking a non-Draft status).
 * - The five `CONSTANT_MANDATORY_FIELDS` always report
 *   `isMandatory(name) === false` and `isVisible(name) === true` from this
 *   selector regardless of what the API map says — their existing
 *   required-by-status logic is the only authority.
 * - Missing fields default to visible / non-mandatory for both standard
 *   fields and custom attributes.
 */
export const useManageFields = (rfiTypeId?: string, status?: RfiStatus): UseManageFieldsReturn => {
  const isFFEnabled = !!featureFlags.get(FEATURE_FLAGS.LIB_MANAGE_FIELDS);
  const { data: rfiTypes } = useGetRfiTypes();

  return useMemo<UseManageFieldsReturn>(() => {
    if (!isFFEnabled) return NOOP_RETURN;

    const { map, hiddenStandardNames, hiddenCustomIds } = resolveActiveManageFields(
      rfiTypes,
      rfiTypeId,
      status,
    );

    const isVisible = (fieldName: string): boolean => {
      // Always-render guarantee: the five core fields short-circuit to
      // visible regardless of what the BE map says. Other fields the API
      // contract marks `canToggleVisibility: false` (e.g. question,
      // dueDate) rely on default-to-visible below — the BE simply never
      // lists them in `manageFields`.
      if (CONSTANT_MANDATORY_FIELDS.includes(fieldName)) return true;
      const field = map[fieldName];
      if (!field) return true; // missing → default visible
      return field.isVisible !== false;
    };

    const isMandatory = (fieldName: string): boolean => {
      // Required-by-status pipeline is the only authority for the core fields.
      if (CONSTANT_MANDATORY_FIELDS.includes(fieldName)) return false;
      const field = map[fieldName];
      if (!field) return false; // missing → default non-mandatory
      return field.isMandatory === true;
    };

    const isCustomVisible = (fieldUuid: string): boolean => {
      const field = map[fieldUuid];
      if (!field) return true; // missing → default visible
      return field.isVisible !== false;
    };

    const isCustomMandatory = (fieldUuid: string): boolean => {
      const field = map[fieldUuid];
      if (!field) return false;
      return field.isMandatory === true;
    };

    return {
      isFFEnabled: true,
      map,
      isVisible,
      isMandatory,
      isCustomVisible,
      isCustomMandatory,
      hiddenStandardNames,
      hiddenCustomIds,
    };
  }, [isFFEnabled, rfiTypeId, status, rfiTypes]);
};
